import { AfterViewInit, DestroyRef, Directive, ElementRef, NgZone, Renderer2, effect, inject, input, output } from '@angular/core';
import { GridConfiguration } from './teiasoft-bucket/teiasoft-grid-odata/teiasoft-grid-odata.component';

class ResizeSelector {
  gridSelector!: string;
  tableSelector!: string;
  headerSelector!: string;
  rowSelector!: string;
}

/**
 * Shared recalculation batching.
 * Every directive that needs to re-apply its configured width (initial layout, shouldRecalculate
 * toggles) registers itself here; one animation frame later the whole grid is processed in a single
 * pass and the width redistribution runs ONCE per grid instead of once per column.
 * Directives are grouped by their parent element (all header cells of a grid share the same parent).
 */
const pendingRecalcGroups: Map<Element, Set<ResizableColumnDirective>> = new Map();
let recalcFlushScheduled: boolean = false;

function scheduleRecalcFlush(): void {
  if (recalcFlushScheduled) return;
  recalcFlushScheduled = true;
  requestAnimationFrame(() => {
    recalcFlushScheduled = false;
    const groups: ResizableColumnDirective[][] = [];
    pendingRecalcGroups.forEach(set => groups.push(Array.from(set)));
    pendingRecalcGroups.clear();

    for (const group of groups) {
      for (const directive of group) {
        directive.applyInitialWidth();
      }
      // One redistribution per grid: it operates on every column, so running it N times is redundant
      const redistributor = group.find(d => d.resizableColumnRedistribute());
      redistributor?.calculateGridWidthAndRedistribute();
    }
  });
}

@Directive({
  selector: '[resizableColumn]'
})
export class ResizableColumnDirective implements AfterViewInit {
  private el: ElementRef<HTMLElement> = inject(ElementRef);
  private renderer: Renderer2 = inject(Renderer2);
  private zone: NgZone = inject(NgZone);

  private startX: number = 0;
  private startWidth: number = 0;
  private columnIndex: number = 0;
  private currWidth: number | undefined;
  private lastPointerX: number = 0;
  private dragRafId: number | null = null;
  private dragging: boolean = false;
  private destroyed: boolean = false;
  private recalcGroupKey: Element | null = null;
  private initialWidthTimeoutId: ReturnType<typeof setTimeout> | undefined;

  columnName = input.required<string>();  // The name of the column
  resizingFlag = input<{ isResizing: boolean }>({ isResizing: false });  // Inject a shared resizing flag
  gridConfiguration = input<GridConfiguration>(new GridConfiguration());
  resizeSelector = input<ResizeSelector | undefined>(undefined);
  shouldRecalculate = input<boolean>(false);
  resizableColumnRedistribute = input<boolean>(false);

  onResizeStart = output<void>();
  onResizeEnd = output<void>();
  onColumnWidthUpdate = output<GridConfiguration>();

  DEFAULT_COLUMN_WIDTH_VW = 7;
  DEFAULT_COLUMN_WIDTH_PX: number = 0;

  constructor() {
    // Reacts to every shouldRecalculate toggle edge (as the previous ngOnChanges did),
    // but the actual work is batched per grid in scheduleRecalcFlush
    effect(() => {
      this.shouldRecalculate();
      this.scheduleRecalculate();
    });

    inject(DestroyRef).onDestroy(() => this.cleanup());
  }

  ngAfterViewInit() {
    const resizer = this.renderer.createElement('span');
    this.renderer.addClass(resizer, 'resizer');
    this.renderer.appendChild(this.el.nativeElement, resizer);
    this.renderer.setStyle(resizer, 'position', 'absolute');
    this.renderer.setStyle(resizer, 'right', '0');
    this.renderer.setStyle(resizer, 'top', '0');
    this.renderer.setStyle(resizer, 'width', '5px');
    this.renderer.setStyle(resizer, 'height', '100%');
    this.renderer.setStyle(resizer, 'cursor', 'col-resize');
    this.renderer.setStyle(resizer, 'user-select', 'none');
    this.renderer.setStyle(resizer, 'z-index', '1');

    // Native listeners registered outside the Angular zone: the whole drag runs without
    // triggering change detection; we re-enter the zone only on mouseup to update the configuration
    this.zone.runOutsideAngular(() => {
      resizer.addEventListener('mousedown', this.onMouseDown);
      resizer.addEventListener('click', this.swallowEvent);
      resizer.addEventListener('dblclick', this.swallowEvent);
    });

    // Load the initial width from the configuration
    this.initialWidthTimeoutId = setTimeout(() => {
      this.scheduleRecalculate();
    }, 200); // this is necessary due to the nature of odata grid that has its own method to calculate its columns widths
  }

  /** Registers this column for the next batched recalculation of its grid */
  scheduleRecalculate(): void {
    if (this.destroyed) return;
    const host = this.el.nativeElement;
    const key = host.parentElement ?? host;
    this.recalcGroupKey = key;
    let group = pendingRecalcGroups.get(key);
    if (!group) {
      group = new Set<ResizableColumnDirective>();
      pendingRecalcGroups.set(key, group);
    }
    group.add(this);
    scheduleRecalcFlush();
  }

  applyInitialWidth() {
    if (this.destroyed) return;
    this.DEFAULT_COLUMN_WIDTH_PX = this.viewportToPixels(this.DEFAULT_COLUMN_WIDTH_VW);

    const gridConfiguration = this.gridConfiguration();
    if (gridConfiguration.gridColumns && gridConfiguration.gridColumns.length > 0) {
      const column = gridConfiguration.gridColumns.find(gc => gc.name?.toLowerCase() == this.columnName().toLowerCase());
      if (!column)
        return;
      if (!column.width) {
        column.width = this.DEFAULT_COLUMN_WIDTH_PX;
      }

      this.setColumnWidthStyles(this.el.nativeElement, column.width);

      this.columnIndex = this.getColumnIndex();
      this.updateColumnCellsWidth(column.width);
    }
    this.onResizeEnd.emit();
  }

  private swallowEvent = (event: Event): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  private onMouseDown = (event: MouseEvent): void => {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.startX = event.pageX;
    this.lastPointerX = event.pageX;
    this.startWidth = this.el.nativeElement.offsetWidth;
    this.currWidth = this.startWidth;
    this.columnIndex = this.getColumnIndex();
    this.resizingFlag().isResizing = true;
    this.dragging = true;

    this.renderer.setStyle(document.body, 'cursor', 'col-resize');
    this.renderer.setStyle(document.body, 'user-select', 'none');

    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  };

  private onMouseMove = (event: MouseEvent): void => {
    this.lastPointerX = event.pageX;
    // DOM writes throttled to one batch per animation frame
    if (this.dragRafId != null) return;
    this.dragRafId = requestAnimationFrame(() => {
      this.dragRafId = null;
      this.applyDragWidth();
    });
  };

  private applyDragWidth(): void {
    const dx = this.lastPointerX - this.startX;
    const newWidth = this.startWidth + dx;

    // GBR 10/07/2024 - Quando il mouse passa il limite ex della colonna non si deve resizare più
    let _minWidth = window.innerWidth * 0.015;
    if (newWidth <= _minWidth) {
      return;
    }
    // END

    this.setColumnWidthStyles(this.el.nativeElement, newWidth);

    this.currWidth = newWidth > 0 ? newWidth : this.viewportToPixels(1); // If the mouse was moved more that the minimum width, let's set the default 2vw width

    this.updateColumnCellsWidth(this.currWidth);
  }

  private onMouseUp = (): void => {
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    this.dragging = false;

    // Apply the last pending pointer position before finalizing
    if (this.dragRafId != null) {
      cancelAnimationFrame(this.dragRafId);
      this.dragRafId = null;
      this.applyDragWidth();
    }

    this.renderer.setStyle(document.body, 'cursor', 'default');
    this.renderer.setStyle(document.body, 'user-select', 'auto');

    this.zone.run(() => {
      // Wait a few ms before declaring the resizing has end to prevent odata onSortClick
      setTimeout(() => {
        this.resizingFlag().isResizing = false;
        const finalWidth = this.currWidth && this.currWidth > 0
          ? this.currWidth
          : this.el.nativeElement.offsetWidth || this.startWidth;

        // Store the new width in gridConfiguration
        const gridConfiguration = this.gridConfiguration();
        gridConfiguration.gridColumns = gridConfiguration.gridColumns?.map(gc => {
          if (gc.name?.toLowerCase() == this.columnName().toLowerCase()) {
            gc.width = finalWidth;
          }
          return gc;
        })

        this.calculateGridWidthAndRedistribute(true);

        this.onColumnWidthUpdate.emit(gridConfiguration);
        this.currWidth = undefined;
      }, 100);
    });
  };

  private cleanup(): void {
    this.destroyed = true;
    if (this.initialWidthTimeoutId != undefined) {
      clearTimeout(this.initialWidthTimeoutId);
    }
    if (this.dragRafId != null) {
      cancelAnimationFrame(this.dragRafId);
      this.dragRafId = null;
    }
    if (this.dragging) {
      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('mouseup', this.onMouseUp);
      this.renderer.setStyle(document.body, 'cursor', 'default');
      this.renderer.setStyle(document.body, 'user-select', 'auto');
      this.dragging = false;
    }
    if (this.recalcGroupKey) {
      pendingRecalcGroups.get(this.recalcGroupKey)?.delete(this);
      this.recalcGroupKey = null;
    }
  }

  getColumnIndex(): number {
    const parent = this.el.nativeElement.parentNode as ParentNode;
    const children = Array.from(parent.children);
    return children.indexOf(this.el.nativeElement);
  }

  updateColumnCellsWidth(width: number) {
    const index = this.columnIndex;

    const resizeSelector = this.resizeSelector();
    if (resizeSelector) {
      const grid = this.el.nativeElement.closest(resizeSelector.gridSelector);
      if (grid) {

        const table = grid.querySelector(resizeSelector.tableSelector);

        if (table) {
          const headers = table.querySelectorAll(resizeSelector.headerSelector)[0].children;
          const rows = table.querySelectorAll(resizeSelector.rowSelector);

          const headerCell = headers[index];
          if (headerCell) {
            this.setColumnWidthStyles(headerCell, width);
          }

          rows.forEach((row) => {
            const cell = row.children[index];
            if (cell) {
              this.setColumnWidthStyles(cell, width);
            }
          });
        }
      }

      return
    }

    // ODATA
    const grid = this.el.nativeElement.closest('teiasoft-grid-odata');
    if (grid) {
      const table = grid.querySelector('.tg-cnt-ext')!;
      const headers = table.querySelectorAll('.tg-header .bg-header')[0].children;

      const headerCell = headers[index];
      if (headerCell && index > 0) {
        this.setColumnWidthStyles(headerCell, width);
      }

      if (table) {
        const rows = table.querySelectorAll('.tg-row-od');
        rows.forEach((row) => {
          const cell = row.children[index];
          if (cell) {
            this.setColumnWidthStyles(cell, width + 1);
          }
        });
      }
    } // DATASHEET
    else {
      const grid = this.el.nativeElement.closest('teiasoft-datasheet');
      if (grid) {

        const table = grid.querySelector('.tds-grid');

        if (table) {
          const headers = table.querySelectorAll('.tds-header')[0].children;
          const rows = table.querySelectorAll('.tds-row');

          const headerCell = headers[index];
          if (headerCell) {
            this.setColumnWidthStyles(headerCell, width);
          }

          rows.forEach((row) => {
            const cell = row.children[index];
            if (cell) {
              this.setColumnWidthStyles(cell, width);
              this.renderer.setStyle(cell, 'border-left', `1px solid transparent`);
            }
          });
        }
      }

    }
  }

  viewportToPixels(vw: number): number {
    let viewportWidth = window.innerWidth / 1; //window.devicePixelRatio;
    return (viewportWidth / 100) * vw;

  }

  private setColumnWidthStyles(element: any, width: number): void {
    const value = `${width}px`;
    this.renderer.setStyle(element, 'width', value);
    this.renderer.setStyle(element, 'min-width', value);
    this.renderer.setStyle(element, 'max-width', value);
    this.renderer.setStyle(element, 'flex-basis', value);
  }

  calculateGridWidthAndRedistribute(shouldPersist: boolean = false) {
    if (!this.resizableColumnRedistribute()) return;

    const resizeSelector = this.resizeSelector();
    const grid = this.el.nativeElement.closest(resizeSelector?.gridSelector || 'teiasoft-grid-odata');
    if (!grid) return;

    const table = grid.querySelector(resizeSelector?.tableSelector || '.tg-cnt-ext');
    const headers = table?.querySelectorAll(resizeSelector?.headerSelector || '.tg-header .bg-header')[0]?.children;

    if (!table || !headers || headers.length <= 1) return;

    const gridWidth = grid.clientWidth;
    const headerWidths: number[] = [];
    let totalColumnWidth = 0;

    // Cache header widths and calculate total column width
    for (let i = 1; i < headers.length; i++) {
      const width = (headers[i] as HTMLElement).offsetWidth;
      headerWidths.push(width);
      totalColumnWidth += width;
    }

    // When we have sticky columns (first header element), subtract its width from the area we can distribute
    const stickyContainer = headers[0] as HTMLElement | undefined;
    const hasStickyContainer = stickyContainer && stickyContainer.classList?.contains('tg-sticky-container');
    const stickyWidth = hasStickyContainer ? stickyContainer!.offsetWidth : 0;
    const availableWidth = Math.max(gridWidth - stickyWidth, 0);

    // If there is extra space, redistribute it to the data columns only
    if (totalColumnWidth < availableWidth) {
      const extraSpace = availableWidth - totalColumnWidth;
      const additionalWidthPerColumn = extraSpace / (headers.length - 1);

      // Precalculate new widths
      const newWidths = headerWidths.map(width => width + additionalWidthPerColumn);

      // Apply new widths to headers
      for (let i = 1; i < headers.length; i++) {
        this.setColumnWidthStyles(headers[i], newWidths[i - 1]);
      }

      // Apply new widths to rows
      const rows = table.querySelectorAll(resizeSelector?.rowSelector || '.tg-row-od');
      rows.forEach((row) => {
        for (let i = 1; i < row.children.length; i++) {
          this.setColumnWidthStyles(row.children[i], newWidths[i - 1]);
        }
      });

      const gridConfiguration = this.gridConfiguration();
      if (shouldPersist && gridConfiguration.gridColumns?.length) {
        const columnsToUpdate = Math.min(gridConfiguration.gridColumns.length, newWidths.length);
        for (let i = 0; i < columnsToUpdate; i++) {
          gridConfiguration.gridColumns[i].width = newWidths[i];
        }
      }
    }

    this.onResizeEnd.emit();
  }


}
